/**
 * Sign in — the one screen with no app shell.
 *
 * The phone centres a single column because that is all it has. A desktop
 * viewport that renders the same column leaves two thirds of the screen empty,
 * so the width is spent on the half of the product an agent has not seen yet:
 * the brand, and what the app is for. The form itself is unchanged — same two
 * fields, same forgot-password call.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as AntApp, Button, Divider, Flex, Form, Input, Typography } from 'antd';
import { LockOutlined, MailOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import * as authApi from '../../api/auth';

const { Text } = Typography;

export default function Login() {
  const { login } = useAuth();
  const { dark, toggle } = useTheme();
  const nav = useNavigate();
  // ProtectedRoute hands over where the agent was heading (e.g. the event QR's
  // check-in link opened by the phone camera); go back there, query and all.
  // A hard redirect after a dead session can't carry router state, so it passes
  // ?next= instead (api/client.js). Same-site paths only.
  const loc = useLocation();
  const from = loc.state?.from;
  const nextParam = new URLSearchParams(loc.search).get('next') || '';
  const safeNext = /^\/(?!\/)/.test(nextParam) && !nextParam.startsWith('/login') ? nextParam : '';
  const back = from?.pathname && from.pathname !== '/login' ? `${from.pathname}${from.search || ''}` : (safeNext || '/');
  const { message } = AntApp.useApp();
  const [mode, setMode] = useState('login');
  const [busy, setBusy] = useState(false);

  const doLogin = async ({ email, password }) => {
    setBusy(true);
    try {
      await login(email, password);
      nav(back, { replace: true });
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async ({ email }) => {
    setBusy(true);
    try {
      await authApi.createAuthForgotPassword({ email });
      message.success('If that email exists, a reset link is on its way');
      setMode('login');
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', height: '100vh' }}>
      <div className="cid-loginhero">
        {/* Sized to the admin portal's login logo (60px), knocked out to white
            by .cid-loginhero — no plate behind it. */}
        <div className="cid-logo" style={{ alignSelf: 'flex-start' }}>
          <img src="/cid_fulllogo.png" alt="CID Realtors" style={{ height: 60 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 520 }}>
          <div style={{ fontSize: 34, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1.2 }}>
            Your whole desk,<br />in one place.
          </div>
          <div style={{ fontSize: 14, opacity: .82, marginTop: 14, lineHeight: 1.7 }}>
            Listings, booking forms, sales, commission and your team — the same CIDAS you carry on
            your phone, laid out for a real screen.
          </div>
        </div>
        <div style={{ fontSize: 11.5, opacity: .6 }}>CID Realtors Sdn Bhd · CIDAS v{__APP_VERSION__}</div>
      </div>

      <Flex vertical justify="center" style={{ padding: 56, background: 'var(--surface)', position: 'relative' }}>
        <Button type="text" style={{ position: 'absolute', top: 20, right: 20 }}
          icon={dark ? <SunOutlined /> : <MoonOutlined />} onClick={toggle} />
        <div style={{ maxWidth: 360, width: '100%', margin: '0 auto' }}>
          {mode === 'login' ? (
            <>
              <div style={{ fontSize: 23, fontWeight: 700, letterSpacing: '-.02em' }}>Welcome back</div>
              <Text type="secondary" style={{ fontSize: 12.5 }}>Sign in to your agent account</Text>
              <Form layout="vertical" size="large" style={{ marginTop: 26 }} onFinish={doLogin} requiredMark={false}>
                <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Enter your email' }]}>
                  <Input placeholder="you@cidrealtors.com" autoComplete="email"
                    prefix={<MailOutlined style={{ color: 'var(--muted)' }} />} />
                </Form.Item>
                <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Enter your password' }]}>
                  <Input.Password placeholder="••••••••" autoComplete="current-password"
                    prefix={<LockOutlined style={{ color: 'var(--muted)' }} />} />
                </Form.Item>
                <Button type="primary" size="large" block htmlType="submit" loading={busy}>Sign in</Button>
              </Form>
              <a style={{ fontSize: 12.5, fontWeight: 500, display: 'inline-block', marginTop: 12 }}
                onClick={() => setMode('forgot')}>Forgot password?</a>
            </>
          ) : (
            <>
              <div style={{ fontSize: 23, fontWeight: 700, letterSpacing: '-.02em' }}>Forgot password</div>
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                Enter your registered email and we&rsquo;ll send a reset link.</Text>
              <Form layout="vertical" size="large" style={{ marginTop: 26 }} onFinish={doForgot} requiredMark={false}>
                <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Enter your email' }]}>
                  <Input placeholder="you@cidrealtors.com" autoComplete="email"
                    prefix={<MailOutlined style={{ color: 'var(--muted)' }} />} />
                </Form.Item>
                <Button type="primary" size="large" block htmlType="submit" loading={busy}>Send reset link</Button>
              </Form>
              <a style={{ fontSize: 12.5, fontWeight: 500, display: 'inline-block', marginTop: 12 }}
                onClick={() => setMode('login')}>Back to sign in</a>
            </>
          )}
          <Divider style={{ margin: '30px 0 16px' }} />
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6 }}>
            Trouble signing in? Your branch admin can reset your password from the admin portal.
          </Text>
        </div>
      </Flex>
    </div>
  );
}
